// src/pages/Watchlists.jsx
import { useMemo, useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import {
  MoreHorizontal, Search as SearchIcon,
  ChevronDown, Trash2
} from "lucide-react";
import WatchlistItemSidebar from "../components/DetailSidebarWatchlists";
import {
  ensureAuthOrRedirect,
  attachExpiryTimer,
  redirectToLogin,
} from "../utils/auth";

const TYPE_OPTIONS = [
  { key: "domain",        label: "Domain" },
  { key: "email",         label: "Email" },
  { key: "ip",            label: "IP Address" },
  { key: "computername",  label: "Computer Name" },
  { key: "github_repo",  label: "Github Repository" },
  { key: "keyword",  label: "Keyword" },
];

// Which audiences to alert (maps to corporate_alerts / consumer_alerts)
const DATA_OPTIONS = [
  { key: "both",      label: "Corporate + Consumer" },
  { key: "corporate", label: "Corporate only" },
  { key: "consumer",  label: "Consumer only" },
];

const placeholders = {
  domain: "E.g. example.com",
  keyword: "E.g. tesla",
  ip: "E.g. 192.x.x5",
  computername: "E.g. HOST-XYZ",
  email: 'E.g elonmusk@gmail.com',
  git_repo: 'E.g https://github.com/OWASP/wrongsecrets.git'
};

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-sm shadow-[0_0_10px_rgba(56,189,248,0.05)]";

export default function Watchlists() {
  // Layout/Sidebar
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("watchlists");
  const [activeTab, setActiveTab] = useState("settings");
  const [listError, setListError] = useState(null);
const [listSuccess, setListSuccess] = useState(null)
   const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
const [credits, setCredits] = useState(null);
  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters/search
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all"); // enabled | disabled | all
  const [type, setType] = useState("all");

// track which row is being deleted (to disable the button)
const [deletingId, setDeletingId] = useState(null);

  // Create identifier form (right panel)
  const [newType, setNewType] = useState("domain");
  const [newEntry, setNewEntry] = useState("");
  const [recordType, setRecordType] = useState("both"); // maps to corporate/consumer toggles
  const [ignored, setIgnored] = useState("");           // used as notify_email (fallback on backend)
  const [includeCsv, setIncludeCsv] = useState(false);  // maps to include_usernames
  const [pushToJira, setPushToJira] = useState(false);
  const [detectLookalike, setDetectLookalike] = useState(false);
  const [pushToSlack, setPushToSlack] = useState(false);
  const [includePasswords, setIncludePasswords] = useState(false);
  const [adding, setAdding] = useState(false);

  // Optional forbidden modal
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  // JWT getter
  function getJwtToken() {
    try {
      return (
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  // Put this near your other helpers (it uses getJwtToken and redirectToLogin)
async function deleteWatchlist(itemOrId, rows = []) {
  const isObj = itemOrId && typeof itemOrId === "object";
  const id = isObj ? itemOrId.id : itemOrId;

  const row = isObj ? itemOrId : (rows.find(x => String(x.id) === String(id)) || null);

  const entry_type = row?.entry_type || row?.type || row?.kind || null;
  const entry = row?.entry || row?.value || row?.identifier || null;

  if (!entry_type || !entry) {
    throw new Error(
      "Missing entry_type/entry. Call deleteWatchlist(row), or pass the row array as the 2nd arg when calling deleteWatchlist(id, rows)."
    );
  }

  const token = getJwtToken();
  if (!token) throw new Error("Authentication required.");

  const res = await fetch(
    "https://whiteintel.io/api/regular/app/v2/watchlist/delete_watchlist_item.php",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ entry_type, entry }),
    }
  );

  if ([401, 419, 440].includes(res.status)) {
    redirectToLogin();
    throw new Error(`AUTH_REDIRECT_${res.status}`);
  }
  if (res.status === 403) {
    throw new Error("You don’t have permission to delete this item.");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Delete failed (${res.status})`);
  }

  const data = await res.json().catch(() => ({}));
  if (data?.success === false) {
    throw new Error(data?.message || "Delete failed.");
  }

  // Return API message so caller can show it to the user
  return {
    success: true,
    id,
    entry_type,
    entry,
    message: data?.message || "Watchlist item deleted.",
  };
}

const handleDelete = async (row) => {
  setListError(null);
  setListSuccess(null);
  setDeletingId(row?.id ?? null);

  try {
    const result = await deleteWatchlist(row); // or deleteWatchlist(row.id, rows)
    // remove from UI (prefer id, fallback to entry+type)
    setRows(prev =>
      prev.filter(x =>
        x.id != null
          ? String(x.id) !== String(row.id)
          : !(x.entry === row.entry && x.entry_type === row.entry_type)
      )
    );
    setListSuccess(result.message || "Watchlist item deleted and credit restored");
  } catch (e) {
    setListError(e.message || "Failed to delete watchlist item");
  } finally {
    fetchCredits();
    setDeletingId(null);
  }
};

  // Fetch watchlist items
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const jwtToken = getJwtToken();

        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwtToken}`,
            },
            body: JSON.stringify({ metric: "all" }),
          }
        );

        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();

        // Expected: array of { entry, entry_type, status, added_at } (may include id)
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setListError(e.message || "Unable to load watchlist items");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

 const fetchCredits = async () => {
    try {
      const jwtToken = getJwtToken();
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ metric: "tokens" }),
        }
      );
      if (!res.ok) throw new Error("Failed to fetch tokens");
      const data = await res.json();
      // API returns [{ domain_tokens, email_tokens, computername_tokens, ip_tokens }]
      const obj = Array.isArray(data) ? data[0] : data;
      setCredits(obj || {});
    } catch (e) {
      console.error("Fetch credits error:", e);
    }
  };
useEffect(() => {

  fetchCredits();
}, []);

const jiraReady = Boolean(credits?.jira_active);
const slackReady = Boolean(credits?.slack_active);
const csvAllowed = Boolean(credits?.csv_allowed ?? false);
const lookalikeAllowed = Boolean(credits?.lookalike_allowed ?? false);
// if an integration becomes inactive, clear its toggle
useEffect(() => {
  if (!jiraReady && pushToJira) setPushToJira(false);
}, [jiraReady]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!slackReady && pushToSlack) setPushToSlack(false);
}, [slackReady]); // eslint-disable-line react-hooks/exhaustive-deps
  // Filtered list
  const filtered = useMemo(() => {
    return rows
      .filter((r) => (status === "all" ? true : r.status === status))
      .filter((r) => (type === "all" ? true : r.entry_type === type))
      .filter((r) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return r.entry?.toLowerCase().includes(q);
      })
      .sort((a, b) => (b.added_at || "").localeCompare(a.added_at || ""));
  }, [rows, status, type, query]);

  // Toggle status (client-side)
const toggleRowStatus = async (id, currentStatus) => {
  const newStatus = currentStatus === "enabled" ? "disabled" : "enabled";

  // optimistic UI update
  setRows((prev) =>
    prev.map((r) =>
      r.id === id ? { ...r, status: newStatus } : r
    )
  );

  try {
    await fetch("https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
      body: JSON.stringify({
        id,
        metric: "status_update",
        status: newStatus,
      }),
    });
  } catch (err) {
    console.error("Failed to update status:", err);
    // rollback on error
    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, status: currentStatus } : r
      )
    );
  }
};



  const resetForm = () => {
    setNewEntry("");
    setRecordType("both");
    setIgnored("");
    setIncludeCsv(false);
    setIncludePasswords(false);
    setPushToJira(false);
    setPushToSlack(false);
  };

  // ---- Add Identifier (API wired) ----
  const addIdentifier = async () => {
    const searchQuery = newEntry.trim();
    if (!searchQuery) return;

    // Map recordType → booleans
    const corporateAlerts = recordType === "both" || recordType === "corporate";
    const consumerAlerts  = recordType === "both" || recordType === "consumer";
    
    const payload = {
      query: searchQuery,
      metric: newType, // domain | email | ip | computername | keyword
      corporate_alerts: corporateAlerts,
      consumer_alerts: consumerAlerts,
      include_usernames: includeCsv,
      push_to_jira: pushToJira,
      push_to_slack: pushToSlack,
      detect_look_alike: detectLookalike,
      notify_email: ignored, // backend will fallback to user email if invalid/empty
    };

    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      const jwtToken = getJwtToken();

      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_manage_handler_v2.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      // Special-case 403 Forbidden search
      if (response.status === 403) {
        if (responseData?.message?.includes("Forbidden search")) {
          setIsForbiddenModalOpen(true);
          return;
        }
      }

      if (!response.ok) {
        const msg =
          responseData?.error ||
          responseData?.message ||
          `Request failed (${response.status})`;
        setError(msg);
        return;
      }

      // Success: prepend optimistically (in case API doesn't echo the new row)
      const newRow = {
        id: responseData?.id, // if backend returns it
        entry: searchQuery,
        entry_type: newType,
        status: "enabled",
        added_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      };
      setRows((prev) => [newRow, ...prev]);
      resetForm();
      fetchCredits();
      setSuccess(responseData?.success || "Entry added to your watchlist.");
    } catch (e) {
      console.error("Add identifier error:", e);
      setError("A network error occurred. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer to align with Sidebar */}
      <header
        className="bg-transparent px-6 py-4 transition-all duration-300"
        style={{ marginLeft: collapsed ? "72px" : "240px" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-[24px] font-bold leading-tight">Watchlists</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        {/* Content */}
        <main className="flex-1 px-4 md:px-6">
          {/* Top bar */}
          <div className="">


            {/* Status/Types row */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {/* Status */}
              <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${frosted}`}>
                <span className="text-neutral-300">Status</span>
                <button
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-white/5"
                  onClick={() =>
                    setStatus((s) =>
                      s === "enabled" ? "disabled" : s === "disabled" ? "all" : "enabled"
                    )
                  }
                  type="button"
                >
                  {status === "all" ? "All" : status[0].toUpperCase() + status.slice(1)}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>

              {/* Types */}
              <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${frosted}`}>
                <span className="text-neutral-300">Types</span>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="bg-neutral-900 text-white outline-none rounded-lg border border-sky-900/30 px-2 py-1 text-sm"
                >
                  <option value="all">All</option>
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Alert banners */}
{listError && (
  <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 backdrop-blur-sm px-3 py-2 text-sm text-red-200 flex items-start justify-between">
    <span className="pr-4">{listError}</span>
    <button
      onClick={() => setListError(null)}
      className="ml-3 text-xs text-neutral-400 hover:text-white"
      aria-label="Dismiss error"
      type="button"
    >
      Dismiss
    </button>
  </div>
)}

{listSuccess && (
  <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-sm px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
    <span className="pr-4">{listSuccess}</span>
    <button
      onClick={() => setListSuccess(null)}
      className="ml-3 text-xs text-neutral-400 hover:text-white"
      aria-label="Dismiss success"
      type="button"
    >
      Dismiss
    </button>
  </div>
)}

 

          {/* Two columns */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-6">
            {/* LEFT: list (search + table) */}
            <div className={`rounded-2xl ${frosted}`}>
              <div className="px-4 pt-4">
                {/* Search */}
                <div className={`relative rounded-xl ${frosted} w-full`}>
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-300" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search in identifiers"
                    className="w-full bg-transparent pl-10 pr-3 py-2.5 text-sm outline-none placeholder-neutral-400"
                  />
                </div>

                {/* Meta */}
                <div className="mt-3 flex items-center justify-between border-b border-sky-900/30 pb-3">
                  <div className="text-sm text-neutral-300">
                    {loading ? "Loading…" : `${filtered.length.toLocaleString()} items`}
                  </div>
                  <div className="text-xs text-neutral-400">Creation Date (desc)</div>
                </div>
              </div>

              <div className="max-h-[70vh] overflow-auto scrollbar-thin scrollbar-thumb-neutral-700/70 scrollbar-track-transparent">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#0a1224]/90 backdrop-blur-sm">
                    <tr className="text-neutral-400 border-b border-sky-900/30">
                      <th className="px-2 py-3 text-left font-semibold">Entry</th>
                      <th className="px-2 py-3 text-left font-semibold">Type</th>
                      <th className="px-2 py-3 text-left font-semibold">Creation Date</th>
                      <th className="px-2 py-3 text-left font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800/80">
                    {listError  ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-10 text-center text-red-400">
                          {listError}
                        </td>
                      </tr>
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-10 text-center text-neutral-400">
                          No identifiers match your filters.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r, idx) => (
                        <tr key={`${r.entry}-${idx}`} className="hover:bg-white/5">
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-2">
                              <div className="font-medium">{r.entry}</div>
                              <div
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border ${
                                  r.status === "enabled"
                                    ? "border-emerald-500/30 text-emerald-200 bg-emerald-500/10"
                                    : "border-amber-500/30 text-amber-200 bg-amber-500/10"
                                }`}
                              >
                                {r.status === "enabled" ? "Enabled" : "Disabled"}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-3 capitalize">{r.entry_type}</td>
                          <td className="px-2 py-3">{r.added_at}</td>
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                className="inline-flex items-center gap-1 text-neutral-300 hover:text-white"
                                onClick={() => toggleRowStatus(r.id, r.status)}
                                title={r.status === "enabled" ? "Disable" : "Enable"}
                              >
                                {r.status === "enabled" ? "Disable" : "Enable"}
                              </button>
               
          <button
            className="rounded-md p-1 hover:bg-white/5 disabled:opacity-50"
            title="Delete"
            onClick={() => handleDelete(r)}
            disabled={deletingId === r.id}
          >
            <Trash2 className="h-4 w-4" />
          </button>

                                                           <button
                                className="rounded-md p-1 hover:bg-white/5"
                                title="More"
                                onClick={() => {
                                    setSelectedId(r.id);   
                                    setSidebarOpen(true);   
                                }}
                                >
                                <MoreHorizontal className="h-4 w-4" />
                                </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT: Create Identifier panel */}
          <div className={`rounded-2xl ${frosted} p-5`}>
      {/* Tabs */}
      <div className="flex border-b border-neutral-800 mb-4">
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "settings"
              ? "text-white border-b-2 border-white"
              : "text-neutral-400 hover:text-white"
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => setActiveTab("definitions")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "definitions"
              ? "text-white border-b-2 border-white"
              : "text-neutral-400 hover:text-white"
          }`}
        >
          Definitions
        </button>
      </div>

               {error && (
            <div className="sticky top-0 z-40 px-0 md:px-2 pt-3 bg-transparent">
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 backdrop-blur-sm px-3 py-2 text-sm text-red-200 flex items-start justify-between">
                <span className="pr-4">{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-3 text-xs text-neutral-400 hover:text-white"
                  aria-label="Dismiss error"
                  type="button"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="sticky top-0 z-40 px-0 md:px-2 pt-3 bg-transparent">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-sm px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
                <span className="pr-4">{success}</span>
                <button
                  onClick={() => setSuccess(null)}
                  className="ml-3 text-xs text-neutral-400 hover:text-white"
                  aria-label="Dismiss success"
                  type="button"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

      {/* Tab Content */}
      {activeTab === "settings" && (
        <div className="space-y-5">
          <div>
            <div className="text-lg font-semibold">Create Identifier</div>
            <div className="text-xs text-neutral-400">
              Add a new identifier to your watchlist
            </div>
          </div>

          {/* Usage */}
<div className="mt-4 rounded-xl border border-sky-900/30 bg-white/[0.04] p-4 text-xs text-neutral-400">
  {credits ? (
    <>
      {newType === "domain" && (
        <span>
          {credits.domain_tokens} Domain identifiers left in your subscription.
          {credits.domain_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "email" && (
        <span>
          {credits.email_tokens} Email identifiers left in your subscription.
          {credits.email_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "ip" && (
        <span>
          {credits.ip_tokens} IP identifiers left in your subscription.
          {credits.ip_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "computername" && (
        <span>
          {credits.computername_tokens} Computer Name identifiers left in your subscription.
          {credits.computername_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "keyword" && (
         <span>
          {credits.keyword_tokens ?? 0} Keyword identifiers left in your subscription.
          {credits.keyword_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

            {newType === "github_repo" && (
         <span>
          {credits?.git_tokens ?? 0} Github Repository  identifiers left in your subscription.
          {credits?.git_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}
    </>
  ) : (
    "Loading subscription usage..."
  )}
</div>


          {/* Type (metric) */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Type
            </label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full rounded-lg border border-sky-900/30 bg-neutral-900 text-white px-3 py-2.5 text-sm outline-none"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Identifier value */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Identifier
            </label>
            <input
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
              placeholder={placeholders[newType] ?? "Enter value"}
              className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2.5 text-sm outline-none"
            />
          </div>

          {/* Record / Audience type */}
                {newType === "domain" && (
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Record Type
                    </label>
                    <select
                      value={recordType}
                      onChange={(e) => setRecordType(e.target.value)}
                      className="w-full rounded-lg border border-sky-900/30 bg-neutral-900 text-white px-3 py-2.5 text-sm outline-none"
                    >
                      {DATA_OPTIONS.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

          {/* Who to notify */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
              WHO TO NOTIFY <span className="text-neutral-500">(YOUR EMAIL IF EMPTY)</span>
            </label>
            <input
              value={ignored}
              onChange={(e) => setIgnored(e.target.value)}
              placeholder="e.g. user@example.com"
              className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2.5 text-sm outline-none"
            />
          </div>

          {/* Checkboxes */}

                  <div className="space-y-2">

                    {/*Look alike domains*/}

                    {newType === "domain" && (
                    <label
  className={`flex items-center gap-2 text-sm ${
    lookalikeAllowed ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={lookalikeAllowed ? "" : "Slack integration not configured"}
>
  <input
    type="checkbox"
    checked={detectLookalike}
    onChange={(e) => setDetectLookalike(e.target.checked)}
    disabled={!lookalikeAllowed}
    aria-disabled={!lookalikeAllowed}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${lookalikeAllowed ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Detect Lookalike Domains
    {!lookalikeAllowed && <span className="ml-1 text-neutral-400">(Higher license required.)</span>}
  </span>
</label>
                             )}         {/* Jira */}

{newType !== "keyword" && (
<label
  className={`flex items-center gap-2 text-sm ${
    jiraReady ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={jiraReady ? "" : "Jira integration not configured"}
>
  <input
    type="checkbox"
    checked={pushToJira}
    onChange={(e) => setPushToJira(e.target.checked)}
    disabled={!jiraReady}
    aria-disabled={!jiraReady}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${jiraReady ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Push to Jira
    {!jiraReady && <span className="ml-1 text-neutral-400">(Not configured yet)</span>}
  </span>
</label>
)}
{/* Slack */}
<label
  className={`flex items-center gap-2 text-sm ${
    slackReady ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={slackReady ? "" : "Slack integration not configured"}
>
  <input
    type="checkbox"
    checked={pushToSlack}
    onChange={(e) => setPushToSlack(e.target.checked)}
    disabled={!slackReady}
    aria-disabled={!slackReady}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${slackReady ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Push to Slack
    {!slackReady && <span className="ml-1 text-neutral-400">(Not configured yet)</span>}
  </span>
</label>
{newType !== "keyword" && newType !== "git_repo" && (
     <label
  className={`flex items-center gap-2 text-sm ${
    csvAllowed ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={csvAllowed ? "" : "Higher license required."}
>
              <input
                type="checkbox"
                checked={includeCsv}
                onChange={(e) => setIncludeCsv(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500"
              />
              Include Usernames as CSV
              {!csvAllowed && <span className="ml-1 text-neutral-400">(Higher license required.)</span>}
            </label>
)}
          </div>


          {/* Actions */}
          <div className="flex justify-between pt-2">
            <button
              onClick={resetForm}
              className="rounded-lg border border-neutral-700 bg-neutral-900/40 px-4 py-2 text-sm text-neutral-200 hover:bg-neutral-900"
            >
              Reset
            </button>
            <button
              onClick={addIdentifier}
              disabled={!newEntry.trim() || adding}
              className={`rounded-lg border px-4 py-2 text-sm ${
                adding
                  ? "border-sky-700/40 bg-sky-700/10 text-sky-300/70 cursor-not-allowed"
                  : "border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
              }`}
            >
              {adding ? "Adding…" : "Add Identifier"}
            </button>
          </div>


        </div>
      )}

      {activeTab === "definitions" && (
        <div className="space-y-4 text-sm text-neutral-300">
          <h3 className="text-lg font-semibold text-white">Identifier Definitions</h3>
          <div>
            <span className="font-semibold text-sky-400">Domain:</span>{" "}
            A registrable domain address (e.g., <code>acme.com</code>). You will
            be notified whenever a credential leak affects the website or any
            account using the domain’s email extension (e.g., user@acme.com).
          </div>
          <div>
            <span className="font-semibold text-sky-400">Email Address:</span>{" "}
            An individual email address to track against info-stealer infections
            and credential leaks.
          </div>
                <div>
            <span className="font-semibold text-sky-400">Look-alike Domain:</span>{" "}
            A domain name intentionally registered to resemble a legitimate company or brand domain (e.g. micros0ft.com instead of microsoft.com). These domains are often used in phishing, impersonation, or malware campaigns to deceive users or collect sensitive information.{" "}
            <span className="text-amber-400 font-semibold">
              Requires an active Enterprise or Threat Intelligence license.
            </span>
          </div>
          <div>
            <span className="font-semibold text-sky-400">IP:</span>{" "}
            An IPv4 or IPv6 address to monitor for info-stealer infection.{" "}
            <span className="text-amber-400 font-semibold">
              Requires an active Threat Intelligence license.
            </span>
          </div>
          
          <div>
            <span className="font-semibold text-sky-400">Computername:</span>{" "}
            A device hostname (e.g. DESKTOP-4KSA) to monito for info-stealer infection.{" "}
            <span className="text-amber-400 font-semibold">
              Requires an active Threat Intelligence license.
            </span>
          </div>

                    <div>
            <span className="font-semibold text-sky-400">Consumer Records:</span>{" "}
            Leaked accounts without company email extension. E.g. user@gmail.com
          </div>
                        <div>
            <span className="font-semibold text-sky-400">Corporate Records:</span>{" "}
            Leaked accounts with company email extension. E.g. user@acme.com
          </div>

                                  <div>
            <span className="font-semibold text-sky-400">Keyword:</span>{" "}
           Keyword to monitor on dark web mentions. E.g. company
          </div>

        </div>
      )}
    </div>
          </div>

        </main>
            <WatchlistItemSidebar
        open={sidebarOpen}
        itemId={selectedId}
        jwtToken={getJwtToken()}
        onClose={() => setSidebarOpen(false)}
        onStatusChange={(id, status) => {
            setRows((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, status } : item
            )
            );
        }}
        />
      </div>

      {/* Minimal Forbidden modal */}
      {isForbiddenModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsForbiddenModalOpen(false)}
          />
          <div className="relative z-[201] max-w-md w-full rounded-xl border border-red-500/30 bg-[#000414] p-5 text-sm">
            <div className="text-red-300 font-semibold mb-2">Forbidden search</div>
            <div className="text-neutral-200">
              This identifier is not allowed to be added. Please contact support if you believe this is an error.
            </div>
            <div className="mt-4 text-right">
              <button
                onClick={() => setIsForbiddenModalOpen(false)}
                className="rounded-lg border border-neutral-700 bg-neutral-900/40 px-3 py-1.5 text-neutral-200 hover:bg-neutral-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
